import { API_METHODS } from '@compass/client/constants/api-methods';
import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { BreachedCase } from '@compass/client/types/cases_analytics';
import { useCanCallAPI } from '@hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { caseRecordService } from '@/services/api';

/** Rows shown per page of the SLA breached cases table. */
export const BREACHED_CASES_PAGE_SIZE = 25;

/**
 * Hydrates ONE visible page of the breached-cases table: the ClickHouse rows carry
 * only ids and timestamps, so the case code and completed time come from the case
 * API. An id the response misses stays absent — the row still renders from its
 * ClickHouse fields. The query is gated on the permission map (as the webhooks
 * pages gate theirs): a case_analytics holder may lack case_record:read, and this
 * app's transport never settles a 403, so an ungated query would load forever.
 */
export const useBreachedCaseHydration = (
  organizationId: string | null,
  pageRows: BreachedCase[],
): { recordById: Map<string, GetCaseRecordResponse>; isLoading: boolean } => {
  const ids = useMemo(
    () => pageRows.map(r => r.case_record_id).filter((id): id is string => !!id),
    [pageRows],
  );
  const canHydrate = useCanCallAPI(API_METHODS.CaseRecordsService.FindOrganizationCaseRecords);

  const query = useQuery({
    queryKey: ['compass', 'cases-analytics', 'breached-hydrate', organizationId ?? '', ids],
    queryFn: () =>
      caseRecordService.findOrganizationCaseRecords({
        organization_id: organizationId!,
        case_record_ids: ids,
        // Every AE/PQC analytics case is a pqc workflow; the org endpoint requires a scope.
        workflow_types: ['pqc'],
        limit: ids.length,
      }),
    enabled: canHydrate && !!organizationId && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const recordById = useMemo(() => {
    const map = new Map<string, GetCaseRecordResponse>();
    for (const record of query.data?.case_records ?? []) {
      if (record.id) map.set(record.id, record);
    }
    return map;
  }, [query.data?.case_records]);

  return { recordById, isLoading: canHydrate && ids.length > 0 && query.isLoading };
};
